import { useState } from 'react';
import { useAuth } from '../hooks/useAuth';

export function AuthForm() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isFocused, setIsFocused] = useState<'email' | 'password' | null>(null);

  const { login, register } = useAuth();

  const validateEmail = (email: string) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const validatePassword = (password: string) => {
    return password.length >= 8;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateEmail(email)) {
      setError('请输入有效的邮箱地址');
      return;
    }

    if (!validatePassword(password)) {
      setError('密码长度至少为8位');
      return;
    }

    setIsLoading(true);

    try {
      if (isLogin) {
        await login(email, password);
      } else {
        await register(email, password);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : '操作失败';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md">
      {/* 玻璃态卡片容器 */}
      <div className="relative backdrop-blur-xl bg-white/70 rounded-3xl shadow-2xl shadow-blue-500/10 border border-white/50 overflow-hidden">
        {/* 顶部装饰渐变条 */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-400 via-indigo-400 to-slate-400"></div>

        <div className="p-10">
          {/* 切换按钮 */}
          <div className="flex mb-8 bg-slate-100/80 rounded-2xl p-1.5">
            <button
              type="button"
              onClick={() => {
                setIsLogin(true);
                setError('');
              }}
              className={`
                flex-1 py-3 px-6 rounded-xl font-semibold text-sm transition-all duration-300
                ${isLogin
                  ? 'bg-white text-slate-900 shadow-lg shadow-slate-200/50'
                  : 'text-slate-600 hover:text-slate-900'
                }
              `}
            >
              登录
            </button>
            <button
              type="button"
              onClick={() => {
                setIsLogin(false);
                setError('');
              }}
              className={`
                flex-1 py-3 px-6 rounded-xl font-semibold text-sm transition-all duration-300
                ${!isLogin
                  ? 'bg-white text-slate-900 shadow-lg shadow-slate-200/50'
                  : 'text-slate-600 hover:text-slate-900'
                }
              `}
            >
              注册
            </button>
          </div>

          {/* 标题 */}
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold bg-gradient-to-r from-slate-900 via-slate-700 to-slate-900 bg-clip-text text-transparent mb-2">
              {isLogin ? '欢迎回来' : '创建账号'}
            </h2>
            <p className="text-slate-600 text-sm">
              {isLogin ? '登录以继续使用 AI PPT 生成器' : '开始您的 AI 创作之旅'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* 邮箱输入 */}
            <div className="space-y-2">
              <label htmlFor="email" className="block text-sm font-semibold text-slate-700 ml-1">
                邮箱地址
              </label>
              <div className="relative">
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onFocus={() => setIsFocused('email')}
                  onBlur={() => setIsFocused(null)}
                  className={`
                    w-full px-5 py-4 rounded-2xl border-2 transition-all duration-200
                    placeholder:text-slate-400
                    ${isFocused === 'email'
                      ? 'border-blue-400 shadow-lg shadow-blue-400/20 bg-white'
                      : 'border-slate-200 bg-white/60 hover:border-slate-300'
                    }
                  `}
                  placeholder="your@email.com"
                  required
                />
                {/* 输入框装饰 */}
                {isFocused === 'email' && (
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-500/5 to-indigo-500/5 pointer-events-none"></div>
                )}
              </div>
            </div>

            {/* 密码输入 */}
            <div className="space-y-2">
              <label htmlFor="password" className="block text-sm font-semibold text-slate-700 ml-1">
                密码
              </label>
              <div className="relative">
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => setIsFocused('password')}
                  onBlur={() => setIsFocused(null)}
                  className={`
                    w-full px-5 py-4 rounded-2xl border-2 transition-all duration-200
                    placeholder:text-slate-400
                    ${isFocused === 'password'
                      ? 'border-blue-400 shadow-lg shadow-blue-400/20 bg-white'
                      : 'border-slate-200 bg-white/60 hover:border-slate-300'
                    }
                  `}
                  placeholder="至少8位密码"
                  required
                />
                {/* 输入框装饰 */}
                {isFocused === 'password' && (
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-500/5 to-indigo-500/5 pointer-events-none"></div>
                )}
              </div>
            </div>

            {/* 错误提示 */}
            {error && (
              <div className="relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-red-500/10 to-orange-500/10"></div>
                <div className="relative bg-red-50/80 backdrop-blur-sm border-2 border-red-200/50 text-red-700 px-5 py-4 rounded-2xl font-medium flex items-center gap-2 animate-shake">
                  <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  {error}
                </div>
              </div>
            )}

            {/* 提交按钮 */}
            <button
              type="submit"
              disabled={isLoading}
              className={`
                w-full py-4 px-8 rounded-2xl font-semibold text-white
                transform transition-all duration-200
                hover:scale-[1.02] active:scale-[0.98]
                disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none
                shadow-lg hover:shadow-xl
                relative overflow-hidden
              `}
              style={{
                background: isLoading
                  ? 'linear-gradient(135deg, #94A3B8 0%, #64748B 100%)'
                  : 'linear-gradient(135deg, #3B82F6 0%, #6366F1 100%)',
              }}
            >
              <span className="relative flex items-center justify-center gap-2">
                {isLoading ? (
                  <>
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12c0-2.34.415-4.56 1.167-6.395L12 20.828l2.833-2.433C15.585 16.56 16 14.34 16 12c0-2.34-.415-4.56-1.167-6.395z" />
                    </svg>
                    处理中...
                  </>
                ) : (
                  <>
                    {isLogin ? '登录' : '注册账号'}
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </>
                )}
              </span>
              {/* 按钮光泽效果 */}
              {!isLoading && (
                <div className="absolute inset-0 bg-gradient-to-r from-white/20 via-transparent to-white/20 pointer-events-none"></div>
              )}
            </button>
          </form>

          {/* 底部提示 */}
          <div className="mt-6 text-center">
            <p className="text-slate-600 text-sm">
              {isLogin ? '还没有账号？' : '已有账号？'}
              <button
                type="button"
                onClick={() => {
                  setIsLogin(!isLogin);
                  setError('');
                }}
                className="ml-1 text-transparent bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all"
              >
                {isLogin ? '立即注册' : '立即登录'}
              </button>
            </p>
          </div>
        </div>

        {/* 底部装饰渐变条 */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-slate-400 via-indigo-400 to-blue-400"></div>
      </div>
    </div>
  );
}
